import { defineComponent, onBeforeUnmount, ref, Teleport } from 'vue'
import { Tooltip as FlowbiteTooltip } from 'flowbite'
import type { ITooltipProps } from './types'
import { useElementRef } from '@/utils/dom/useElementRef'

// Unique ID generator
let idCounter = 0
const getUniqueId = () => {
  idCounter += 1
  return `tooltipContent-${idCounter}`
}

const Tooltip = defineComponent<ITooltipProps>({
  setup(props, { slots }) {
    const instance = ref<FlowbiteTooltip | undefined>(undefined)
    const tooltipContentId = getUniqueId()

    const mountTooltip = (triggerEl: HTMLElement, mountEl: HTMLElement) => {
      if (instance.value) return

      const options = {
        placement: props.placement || 'top',
        triggerType: 'hover'
      }

      instance.value = new FlowbiteTooltip(mountEl, triggerEl, options as any)
    }

    const triggerElement = useElementRef((el) => {
      if (props.disabled) return
      const contentEl = document.getElementById(tooltipContentId) as any
      if (contentEl) {
        mountTooltip(el, contentEl)
      }
    })

    // Flowbite keeps popper.js listeners + a position observer on the
    // teleported node, and registers itself in a module-level instance map
    // keyed by the content id. Tear both down before Vue unmounts the
    // Teleport so (a) the registry doesn't leak across route transitions and
    // (b) Vue's vnode tree stays consistent — otherwise Teleport unmount can
    // hit a Vue bug where `isAsyncWrapper` reads `.type` on an undefined
    // vnode (vuejs/core#4075, vuejs/core#12702).
    onBeforeUnmount(() => {
      instance.value?.destroyAndRemoveInstance()
      instance.value = undefined
    })

    return () => (
      <div>
        <div ref={triggerElement.ref}>{slots.default ? slots.default() : null}</div>
        <Teleport to="body">
          {!props.disabled && (
            <div
              id={tooltipContentId}
              role="tooltip"
              style={{ zIndex: 9999 }}
              class={`absolute invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-600 rounded-lg shadow-xs opacity-0 tooltip dark:bg-gray-700 ${props.customClass}`}
            >
              {props.body ? <props.body /> : props.text}
              <div class="tooltip-arrow" data-popper-arrow></div>
            </div>
          )}
        </Teleport>
      </div>
    )
  }
})

Tooltip.props = ['text', 'placement', 'disabled', 'customClass', 'body']

export default Tooltip
